import React, { ComponentProps, useCallback, useState } from 'react';
import {
  SegmentedControl,
  Sidecar,
  Typography,
  Box,
  Divider,
  PageSpinner,
  IconButton,
} from '@fullstory/components';
import { useAlertOverlay, useAlertBell, segmentedControls } from './hooks';
import { MyAlerts } from '../../components/MyAlerts';
import { TeamAlerts } from '../../components/TeamAlerts';
import { NotificationsList } from '../../components/NotificationList';
import { useIntl } from 'react-intl';

export const AlertsSidecarContent: React.FC<
  Pick<React.ComponentProps<typeof Sidecar>, 'onClose'> & {
    email: string;
  }
> = ({ onClose, email }) => {
  const {
    myAlerts,
    teamAlerts,
    segments,
    notifications,
    segmentControlOptions,
    orgId,
    lastKnownSegment,
    loading,
    hideNotifications,
    alertLabel,
  } = useAlertOverlay(email);
  const [selected, setSelected] = useState(segmentControlOptions[0]!);
  if (loading) return <PageSpinner diameter={24} />;
  return (
    <>
      <Box padding={16}>
        <Typography variant="heading2" textAlign="center">
          {alertLabel}
        </Typography>
      </Box>
      <Divider />
      {!hideNotifications && (
        <Box padding={[16, 24, 16, 24]}>
          <SegmentedControl
            size="small"
            options={segmentControlOptions}
            selected={selected}
            onChange={s => setSelected(s)}
          />
        </Box>
      )}
      {!hideNotifications && selected?.id === segmentedControls.notifications ? (
        <NotificationsList
          notifications={notifications}
          segments={segments}
          onCloseSignal={onClose}
          orgId={orgId}
        />
      ) : (
        <div>
          <MyAlerts
            myAlerts={myAlerts}
            segments={segments}
            onCloseSignal={onClose}
            orgId={orgId}
            lastKnownSegment={lastKnownSegment}
          />
          <TeamAlerts
            teamAlerts={teamAlerts}
            segments={segments}
            onCloseSignal={onClose}
            orgId={orgId}
          />
        </div>
      )}
    </>
  );
};
AlertsSidecarContent.displayName = 'AlertsSidecarContent';

export const AlertsBell: React.FC<{
  email: string;
  iconSize?: ComponentProps<typeof IconButton>['size'];
  sidecarPosition?: ComponentProps<typeof Sidecar>['position'];
}> = ({ email, iconSize = 'large', sidecarPosition = 'right' }) => {
  const intl = useIntl();
  const { visible, setVisible, notification } = useAlertBell();
  const setInvisible = useCallback(() => setVisible(false), [setVisible]);

  return (
    <>
      <IconButton
        iconName={notification ? 'bell-dot' : 'bell'}
        title={intl.formatMessage({
          defaultMessage: 'Notifications and Alerts',
          id: 'U6GrGz',
        })}
        onClick={() => {
          setVisible(true);
        }}
        size={iconSize}
      />
      <Sidecar open={visible} onClose={setInvisible} position={sidecarPosition}>
        <AlertsSidecarContent onClose={setInvisible} email={email} />
      </Sidecar>
    </>
  );
};

export const BellFallback: React.FC<{
  onClick: () => void;
  iconSize?: ComponentProps<typeof IconButton>['size'];
}> = ({ onClick, iconSize = 'large' }) => {
  const intl = useIntl();

  return (
    <>
      <IconButton
        iconName="bell"
        title={intl.formatMessage({
          defaultMessage: 'Notifications and Alerts',
          id: 'U6GrGz',
        })}
        onClick={onClick}
        size={iconSize}
      />
    </>
  );
};
export * from './hooks';
